import { useContext, useEffect, useState } from 'react';
import { MdOutlineRecycling } from 'react-icons/md';
import { FormattedMessage, useIntl } from 'react-intl';

import { TerminalContext, TerminalContextInterface } from '../../TerminalInit';
import { Slot, useSlots } from '../../api/slots';
import { useSpotLayout } from '../../api/spotLayouts';
import { SpotType, useMutateSpotRecyclePickup, useSpot } from '../../api/spots';
import { TerminalWorkflowType } from '../../api/terminals';
import ActionCard from '../../components/domain/ActionCard';
import { AllowedActionViewProps } from '../../components/domain/AllowedAction';
import useSpotLayoutItemsForSlots from '../../hooks/useSpotLayoutItemForSlot';
import useTerminal from '../../hooks/useTerminal';
import { isDelivery } from '../../services/contact/GlobalPermissions';
import { SlotOpenState, SlotOpenView } from '../../views/SlotOpenView';
import BackAndHomeNavigationButtons from '../../views/common/BackAndHomeNavigationButtons';
import BaseView from '../../views/common/BaseView';
import InfoView from '../../views/common/InfoView';
import LoadingView from '../../views/common/LoadingView';
import SlotOpenSpotLayout from '../../views/spotlayout/slotOpenSpotLayouts/SlotOpenSpotLayout';

type DelhaizeRecyclePickupActionProps = AllowedActionViewProps;

const DelhaizeRecyclePickupView = (props: DelhaizeRecyclePickupActionProps) => {
    const intl = useIntl();
    const { terminal } = useContext<TerminalContextInterface>(TerminalContext);
    const { data: spotLayout } = useSpotLayout(terminal?.spot_layout_url);
    const { data: spot } = useSpot(spotLayout?.spot_url);
    const { data: recycleSlots } = useSlots({ spot: spot?.id, type: 'pickup_recycle' }, { enabled: !!spot });
    const [slotsConfirmed, changeSlotsConfirmed] = useState<boolean>(false);
    const [error, changeError] = useState<string | null>(null);
    const [slots, changeSlots] = useState<Slot[] | null>(null);
    const slotItems = useSpotLayoutItemsForSlots(recycleSlots ?? []);

    const backHomeNav = (
        <BackAndHomeNavigationButtons
            onHome={props.onHome}
            onBack={props.onBack}
        />
    );

    const mutateSpotRecyclePickup = useMutateSpotRecyclePickup({
        onSuccess: (result) => {
            changeSlots(result);
        },
        onError: (error) => {
            changeError(
                intl.formatMessage({
                    id: 'workflow.UserSpecificActions.DelhaizeRecyclePickupAction.noTransactionFound',
                    description: 'Error message when no recycle transaction is found for Pick-up',
                    defaultMessage: 'No recycle transaction found for Pick-up.',
                })
            );
        },
    });

    useEffect(() => {
        if (spot && slotsConfirmed) {
            mutateSpotRecyclePickup.mutate({
                spot_id: spot.id,
            });
        }
    }, [spot, slotsConfirmed]);

    if (error)
        return (
            <InfoView
                navbarItems={backHomeNav}
                onInactivity={props.onInactivity}
                title={error}
            />
        );

    if (!recycleSlots || slotItems === undefined) {
        return (
            <LoadingView
                onInactivity={props.onInactivity}
                onHome={props.onHome}
                onBack={props.onBack}
            />
        );
    }

    if (!slotsConfirmed) {
        return (
            <BaseView
                onInactivity={props.onInactivity}
                navbarItems={backHomeNav}>
                <div className='text-center'>
                    <button
                        className='primary-button btn-lg'
                        onClick={() => changeSlotsConfirmed(true)}>
                        <FormattedMessage
                            id='views.SlotConfirmationView.confirmButton'
                            description='The button label to confirm using the proposed Slot.'
                            defaultMessage='Confirm and open SLOT'
                        />
                    </button>
                </div>
                <SlotOpenSpotLayout
                    type={SpotType.STANDARD_DISTRISPOT}
                    highlightedItems={slotItems}
                />
            </BaseView>
        );
    }

    if (!slots) {
        return (
            <LoadingView
                onInactivity={props.onInactivity}
                onHome={props.onHome}
                onBack={props.onBack}
            />
        );
    }

    return (
        <SlotOpenView
            spotType={spot ? spot.type : SpotType.STANDARD_DISTRISPOT}
            slotInfos={slots.map((s) => {
                return { slot: s, state: SlotOpenState.SUCCESS };
            })}
            onInactivity={props.onInactivity}
            onHome={props.onHome}
        />
    );
};

function DelhaizeRecyclePickupAction(props: DelhaizeRecyclePickupActionProps) {
    const intl = useIntl();
    const terminal = useTerminal();

    if (terminal === null || props.contact === undefined) return undefined;
    if (!(terminal.workflow === TerminalWorkflowType.DELHAIZE && isDelivery(props.contact))) return undefined;

    const delhaizeRecyclePickupAction: ActionCard = {
        actionText: intl.formatMessage({
            id: 'workflow.UserSpecificActions.DelhaizeRecyclePickupAction',
            description: 'This is the Recycle Pick-up action for UserSpecificActions',
            defaultMessage: 'Recycle Pick-up',
        }),
        highlight: true,
        icon: (
            <MdOutlineRecycling
                className='dashboard-action-icon'
                size={264}
            />
        ),
        view: <DelhaizeRecyclePickupView {...props} />,
    };

    return delhaizeRecyclePickupAction;
}

export default DelhaizeRecyclePickupAction;
